import { algoAnalizaDeImpact } from "./algoAnalizaImpact.ts";
import type { CompanyData } from "./nis2-classifier.ts";

// VERSION 2.3 from DNSC

export enum SIZE_ENUM {
  MICRO_SMALL = "Mică",
  MEDIUM = "Mijlocie",
  LARGE = "Mare",
}

export enum RESULT_ENUM {
  OUT_OF_SCOPE = "OUT_OF_SCOPE",
  IMPORTANT = "IMPORTANT",
  ESENTIAL = "ESSENTIAL",
  IMPACT = "IMPACT",
}

export interface Article9 {
  aSoleProviderEssentialService: boolean;
  bSignificantServiceDisruptionImpact: "RIDICAT" | "MEDIU" | "SCAZUT" | null;
  cServiceDisruptionCrossBorderRisk: "RIDICAT" | "MEDIU" | "SCAZUT" | null;
  dCriticalEntitySectorImportance: boolean;
}

export interface RomanianStateRelationship {
  entityEstablishedInRomania: boolean;
  entityHeadquarteredInRomania: boolean;
  entityProvidesDomainRegistrationInRomania: boolean;
  entityProvidesPublicElectronicCommunicationInRomania: boolean;
  entityIsPublicAdministrationEntityRomania: boolean;
  entityIdentifiedAsCriticalRomania: boolean;
}

export interface EUServiceProvisionStatus {
  providesServicesInRomania: boolean;
  providesServicesInOtherEuMemberState: boolean;
}

export interface CompanyInfo {
  employeesYearlyMedian: number;
  turnover: number;
  totalHoldings: number;
  size: SIZE_ENUM;
  services: string[];
  article9: Article9;
  romanianStateRelationship: RomanianStateRelationship;
  euServiceProvisionStatus: EUServiceProvisionStatus;
  areAnalizaDeImpact: boolean;
}

export interface ClasificareRule {
  description: string;
  type: string;
  services: string[];
  size: {
    [key: string]: (company: CompanyInfo) => {
      result: RESULT_ENUM;
      reason: string;
    };
  };
}

export interface EvaluationResult {
  type?: string;
  serviceId: string;
  description: string;
  size: SIZE_ENUM;
  result: RESULT_ENUM;
  reason: string;
}

export const clasificare: ClasificareRule[] = [
  {
    type: "2",
    description: "Administrație Publică Centrală RO",
    services: ["110001"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Entitate de administrație publică mica",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Entitate de administrație publică mijlocie",
      }),
      [SIZE_ENUM.LARGE]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Entitate de administrație publică mare",
      }),
    },
  },
  {
    type: "3",
    description: "Prestatori calificati",
    services: ["108008"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Prestator calificat mic",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Prestator calificat mijlociu",
      }),
      [SIZE_ENUM.LARGE]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.ESENTIAL,
              reason: "Prestator calificat mare stabilit în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
    },
  },
  {
    type: "4",
    description: "DNS, TLD",
    services: ["108002", "108003"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "DNS, TLD mic",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "DNS, TLD mijlociu",
      }),
      [SIZE_ENUM.LARGE]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "DNS, TLD mare",
      }),
    },
  },
  {
    type: "5",
    description: "Prestatorii necalificati",
    services: ["108007"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Prestator necalificat mic stabilit în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.MEDIUM]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Prestator necalificat mijlociu stabilit în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.LARGE]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.ESENTIAL,
              reason: "Prestator necalificat mare stabilit în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
    },
  },
  {
    type: "6",
    description: "Telecomunicații",
    services: ["108009", "108010"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: (company) =>
        company.euServiceProvisionStatus.providesServicesInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Telecomunicații mic cu servicii în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.MEDIUM]: (company) =>
        company.euServiceProvisionStatus.providesServicesInRomania
          ? {
              result: RESULT_ENUM.ESENTIAL,
              reason: "Telecomunicații mijlociu cu servicii în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.LARGE]: (company) =>
        company.euServiceProvisionStatus.providesServicesInRomania
          ? {
              result: RESULT_ENUM.ESENTIAL,
              reason: "Telecomunicații mare cu servicii în România",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
    },
  },
  {
    type: "7",
    description: "MSSP",
    services: ["109002"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.OUT_OF_SCOPE,
        reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "MSSP mijlociu",
      }),
      [SIZE_ENUM.LARGE]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "MSSP mare",
      }),
    },
  },
  {
    type: "8",
    description:
      "furnizorii de servicii de cloud computing, furnizorii de servicii de centre de date, furnizorii de rețele de furnizare de conținut, furnizorii de servicii gestionate",
    services: ["108004", "108005", "108006", "109001"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.OUT_OF_SCOPE,
        reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.IMPORTANT,
        reason: "Furnizor de servicii mijlociu",
      }),
      [SIZE_ENUM.LARGE]: () => ({
        result: RESULT_ENUM.ESENTIAL,
        reason: "Furnizor de servicii mare",
      }),
    },
  },
  {
    type: "9",
    description:
      "energie, transport, bancar, financiar, sanatate, potabila, uzate, spațial, IXP",
    services: [
      "101101",
      "101102",
      "101103",
      "101104",
      "101105",
      "101106",
      "101107",
      "101108",
      "101201",
      "101301",
      "101302",
      "101303",
      "101401",
      "101402",
      "101403",
      "101404",
      "101405",
      "101406",
      "101407",
      "101501",
      "101601",
      "102101",
      "102102",
      "102103",
      "102104",
      "102105",
      "102106",
      "102107",
      "102201",
      "102202",
      "102301",
      "102302",
      "102303",
      "102401",
      "102402",
      "102501",
      "103001",
      "104001",
      "104002",
      "104003",
      "105001",
      "105002",
      "105003",
      "105004",
      "105005",
      "105006",
      "105007",
      "106001",
      "107001",
      "108001",
      "111001",
    ],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.OUT_OF_SCOPE,
        reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.MEDIUM]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Entitatea îndeplinește criteriile directivei NIS2",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.LARGE]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.ESENTIAL,
              reason: "Entitatea îndeplinește criteriile directivei NIS2",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
    },
  },
  {
    type: "11",
    description: "Toate din Anexa 2",
    services: [
      "201001",
      "202001",
      "203001",
      "204001",
      "205101",
      "205201",
      "205301",
      "205401",
      "205501",
      "205601",
      "207001",
    ],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.OUT_OF_SCOPE,
        reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.MEDIUM]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Entitatea îndeplinește criteriile directivei NIS2",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
      [SIZE_ENUM.LARGE]: (company) =>
        company.romanianStateRelationship.entityEstablishedInRomania
          ? {
              result: RESULT_ENUM.IMPORTANT,
              reason: "Entitatea îndeplinește criteriile directivei NIS2",
            }
          : {
              result: RESULT_ENUM.OUT_OF_SCOPE,
              reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
            },
    },
  },
  {
    type: "12",
    description:
      "furnizorii de piețe online, de motoare de căutare online și de platforme de servicii de socializare în rețea",
    services: ["206001", "206002", "206003"],
    size: {
      [SIZE_ENUM.MICRO_SMALL]: () => ({
        result: RESULT_ENUM.OUT_OF_SCOPE,
        reason: "Entitatea nu îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.MEDIUM]: () => ({
        result: RESULT_ENUM.IMPORTANT,
        reason: "Entitatea îndeplinește criteriile directivei NIS2",
      }),
      [SIZE_ENUM.LARGE]: () => ({
        result: RESULT_ENUM.IMPORTANT,
        reason: "Entitatea îndeplinește criteriile directivei NIS2",
      }),
    },
  },
];

/**
 * Evaluates a company against the classification rules.
 * @param {object} company
 * @returns {Array} Array of evaluation results.
 */
export function evaluateCompany(company: CompanyInfo): EvaluationResult[] {
  const results: EvaluationResult[] = [];
  company.services.forEach((serviceId) => {
    clasificare.forEach((rule) => {
      if (rule.services.includes(serviceId)) {
        const sizeFormula = rule.size[company.size];
        if (typeof sizeFormula === "function") {
          const formulaResult = sizeFormula(company);
          results.push({
            serviceId,
            type: rule.type,
            description: rule.description,
            size: company.size,
            result: formulaResult.result,
            reason: formulaResult.reason,
          });
        }
      }
    });
  });

  // furnizorii de piețe online, de motoare de căutare online și de platforme de servicii de socializare în rețea
  const type12Results = results.filter((r) => r.type === "12");
  if (type12Results.length > 0) {
    if (company.article9.dCriticalEntitySectorImportance) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.ESENTIAL,
        reason: "Infrastructură critică",
      });
    }
    if (
      company.article9.bSignificantServiceDisruptionImpact === "MEDIU" ||
      company.article9.bSignificantServiceDisruptionImpact === "RIDICAT" ||
      company.article9.cServiceDisruptionCrossBorderRisk === "MEDIU" ||
      company.article9.cServiceDisruptionCrossBorderRisk === "RIDICAT" ||
      company.article9.aSoleProviderEssentialService
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.IMPORTANT,
        reason: "Infrastructură importantă",
      });
    }
  }

  //Toate din Anexa 2
  const type11Results = results.filter((r) => r.type === "11");
  if (type11Results.length > 0) {
    if (
      company.article9.dCriticalEntitySectorImportance &&
      company.romanianStateRelationship.entityEstablishedInRomania
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.ESENTIAL,
        reason: "Infrastructură critică stabilită în România",
      });
    }
    if (
      company.romanianStateRelationship.entityEstablishedInRomania &&
      (company.article9.aSoleProviderEssentialService ||
        company.article9.bSignificantServiceDisruptionImpact === "MEDIU" ||
        company.article9.bSignificantServiceDisruptionImpact === "RIDICAT" ||
        company.article9.cServiceDisruptionCrossBorderRisk === "MEDIU" ||
        company.article9.cServiceDisruptionCrossBorderRisk === "RIDICAT")
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.IMPORTANT,
        reason: "Infrastructură importanta stabilită în România",
      });
    }
  }

  // furnizorii de servicii de cloud computing, furnizorii de servicii de centre de date, furnizorii de rețele de furnizare de conținut, furnizorii de servicii gestionate
  const type8Results = results.filter((r) => r.type === "8");
  if (type8Results.length > 0) {
    if (
      company.article9.aSoleProviderEssentialService ||
      company.article9.dCriticalEntitySectorImportance ||
      company.article9.bSignificantServiceDisruptionImpact === "RIDICAT" ||
      company.article9.cServiceDisruptionCrossBorderRisk === "RIDICAT"
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.ESENTIAL,
        reason: "Infrastructură critică",
      });
    }
    if (
      company.size === SIZE_ENUM.MICRO_SMALL &&
      company.romanianStateRelationship.entityHeadquarteredInRomania &&
      (company.article9.bSignificantServiceDisruptionImpact === "MEDIU" ||
        company.article9.cServiceDisruptionCrossBorderRisk === "MEDIU")
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.IMPORTANT,
        reason: "Infrastructură importanta cu sediu în România",
      });
    }
  }

  //energie, transport, bancar, financiar, sanatate, potabila, uzate, spațial, IXP
  const type9Results = results.filter((r) => r.type === "9");
  //MSSP
  const type7Results = results.filter((r) => r.type === "7");

  if (type9Results.length > 0 || type7Results.length > 0) {
    if (
      company.romanianStateRelationship.entityEstablishedInRomania &&
      (company.article9.aSoleProviderEssentialService ||
        company.article9.dCriticalEntitySectorImportance ||
        company.article9.bSignificantServiceDisruptionImpact === "RIDICAT" ||
        company.article9.cServiceDisruptionCrossBorderRisk === "RIDICAT")
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.ESENTIAL,
        reason: "Infrastructură critică stabilită în România",
      });
    }
    if (
      company.romanianStateRelationship.entityEstablishedInRomania &&
      (company.article9.bSignificantServiceDisruptionImpact === "MEDIU" ||
        company.article9.cServiceDisruptionCrossBorderRisk === "MEDIU")
    ) {
      results.push({
        serviceId: null,
        description: "Articol 9 - Evaluare suplimentara",
        size: company.size,
        result: RESULT_ENUM.IMPORTANT,
        reason: "Infrastructură importantă stabilită în România",
      });
    }
  }

  // Verificare Entitatea este/a fost identificată ca entitate critică în România (conform dispozițiilor Legii nr. 294/2024)?
  if (company.romanianStateRelationship.entityIdentifiedAsCriticalRomania) {
    results.push({
      serviceId: null,
      description:
        "Entitatea este/a fost identificată ca entitate critică în România (conform dispozițiilor Legii nr. 294/2024)?",
      size: company.size,
      result: RESULT_ENUM.ESENTIAL,
      reason: "Entitate identificată ca entitate critică în România",
    });
  }

  // Verificare Entitate articol 9
  // TODO: If in all results we don't have any ESENTIAL or IMPORTANT, then check article 9 conditions
  const hasEsentialOrImportant = results.some(
    (r) =>
      r.result === RESULT_ENUM.ESENTIAL || r.result === RESULT_ENUM.IMPORTANT
  );
  // TODO: Check if any results are of any type 8,9,1,12
  const hasArticle9Types = results.some((r) =>
    ["7", "8", "9", "11", "12"].includes(r.type || "")
  );

  if (
    !company.areAnalizaDeImpact &&
    !hasEsentialOrImportant &&
    company.size === SIZE_ENUM.MICRO_SMALL &&
    hasArticle9Types
  ) {
    results.push({
      serviceId: null,
      description: "Articol 9 - Evaluare suplimentara",
      size: company.size,
      result: RESULT_ENUM.IMPACT,
      reason: "Este necesară o evaluare suplimentară conform Articolului 9",
    });
  }

  return results;
}

export const prioritateAnaliza: Record<RESULT_ENUM, number> = {
  [RESULT_ENUM.IMPACT]: 4,
  [RESULT_ENUM.ESENTIAL]: 3,
  [RESULT_ENUM.IMPORTANT]: 2,
  [RESULT_ENUM.OUT_OF_SCOPE]: 1,
};

export function getFinalClassification(
  evaluations: EvaluationResult[]
): RESULT_ENUM {
  let finalResult: RESULT_ENUM = RESULT_ENUM.OUT_OF_SCOPE;
  evaluations.forEach((evaluation) => {
    if (prioritateAnaliza[evaluation.result] > prioritateAnaliza[finalResult]) {
      finalResult = evaluation.result;
    }
  });
  return finalResult;
}

export type CompanyClassification = {
  classification: RESULT_ENUM;
  evaluations: EvaluationResult[];
};

export function getClassification(company: CompanyData): CompanyClassification {
  let impact: {
    "9a": boolean | "" | null;
    "9b": "RIDICAT" | "MEDIU" | "SCAZUT" | "" | null;
    "9c": "RIDICAT" | "MEDIU" | "SCAZUT" | "" | null;
    "9d": boolean | "" | null;
  } = {
    "9a": null,
    "9b": null,
    "9c": null,
    "9d": null,
  };
  if (company.article9Override) {
    impact = company.article9Override;
  } else if (company.analizaImpact) {
    impact = algoAnalizaDeImpact(company.analizaImpact);
  }
  const hasArticle9Data = !!(company.article9Override || company.analizaImpact);
  const companyMapped: CompanyInfo = {
    areAnalizaDeImpact: hasArticle9Data,
    employeesYearlyMedian: 0, // NO
    turnover: 0, // NO
    totalHoldings: 0, // NO
    size: company.companySize as SIZE_ENUM,
    services: company.services,
    article9: {
      aSoleProviderEssentialService: impact["9a"] === "" ? false : impact["9a"],
      bSignificantServiceDisruptionImpact:
        impact["9b"] === "" ? null : impact["9b"],
      cServiceDisruptionCrossBorderRisk:
        impact["9c"] === "" ? null : impact["9c"],
      dCriticalEntitySectorImportance:
        impact["9d"] === "" ? false : impact["9d"],
    },
    romanianStateRelationship: {
      entityEstablishedInRomania: company.establishedInRomania,
      // v2.3: entityHeadquarteredInRomania (D151) removed as a gate for types 4, 7, 8, 12.
      // Still used in the Art.9 type-8 MICRO_SMALL MEDIU path (line ~506).
      entityHeadquarteredInRomania: company.headquarteredInRomania ?? true,
      entityProvidesDomainRegistrationInRomania: false,
      entityProvidesPublicElectronicCommunicationInRomania: false,
      entityIsPublicAdministrationEntityRomania:
        company.isPublicAdminEntityRomania ?? false,
      entityIdentifiedAsCriticalRomania: company.isNationalCritical,
    },
    euServiceProvisionStatus: {
      providesServicesInRomania: company.providesServicesInRomania,
      providesServicesInOtherEuMemberState:
        company.providesServicesInEuMemberState ?? false,
    },
  };
  const companyEvaluations = evaluateCompany(companyMapped);
  const classification = getFinalClassification(companyEvaluations);
  return { classification, evaluations: companyEvaluations };
}
